import { useWidget, VoteAPIResponseCode } from '@telescope/cassini-hooks';
import { Button, Container, Heading, Spinner, useBreakpointValue } from '@telescope/cassini-ui';
import { Text } from 'components';
import { ErrorPage, ThankYou, useAuth, useModals } from 'features';
import { Open } from 'generated-types/widget';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorType } from 'types';
import { ROUTES } from 'utils';
import { useBracketGrid } from '../providers/BracketGridProvider';

export const VoteHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useWidget<Open>({ select: (data) => data.snapshot.snapshot_views.bracketViewHeader });
  const { content, styles } = data;
  const {
    totalAllocatedVotes,
    totalVotesRequired,
    resetVotes,
    hasReachedVoteLimit,
    submitVotes,
    isDirty,
    voteLimit,
    setPendingBracketVote,
    pendingBracketVote,
    bracketType,
  } = useBracketGrid();
  const canSubmitBracket = !hasReachedVoteLimit && totalAllocatedVotes === totalVotesRequired;
  const allMatchupsVoted = bracketType === 'BY_MATCHUP' && totalAllocatedVotes === totalVotesRequired;
  const desktopStyles = useBreakpointValue({
    lg: { position: 'absolute', top: '100px', left: 0, right: 0, maxW: '650px', zIndex: 1 },
  });

  // TODO: (Cassini Hooks) useVote - return loading state
  const [isLoading, setIsLoading] = useState(false);
  const { openModal, closeAll } = useModals();

  // If user isn't logged in yet, we need to log them before they submit. This will mark their submission as 'pending' and will be submitted automatically after they login
  const handleLogin = () => {
    setPendingBracketVote(true);
    return navigate(ROUTES.LOGIN, { state: { backgroundLocation: ROUTES.HOME } });
  };

  const onVoteAgain = useCallback(() => {
    resetVotes();
    closeAll();
  }, [closeAll, resetVotes]);

  const onSubmit = useCallback(async () => {
    let error = null;
    setIsLoading(true);

    try {
      const res = await submitVotes();

      setIsLoading(false);

      if (res.response_code === VoteAPIResponseCode.VALID) {
        const { total } = JSON.parse(res.votestring!);

        return openModal({
          children: (
            <ThankYou onVoteAgain={onVoteAgain} onViewVotes={closeAll} isVoteAgainAllowed={total < voteLimit} />
          ),
          overlayProps: {
            bg: 'rgba(0, 0, 0, 1)',
          },
        });
      }

      const errors = {
        [VoteAPIResponseCode.WINDOW]: ErrorType.WINDOW,
        [VoteAPIResponseCode.OVERLIMIT]: ErrorType.OVERLIMIT,
        [VoteAPIResponseCode.GENERAL_INVALID]: ErrorType.GENERIC,
      };

      // @ts-expect-error: res.response_code might not be a property of errors
      // In this case use the known property VoteAPIResponseCode.GENERAL_INVALID
      error = errors[res.response_code] || errors[VoteAPIResponseCode.GENERAL_INVALID];
    } catch (err) {
      setIsLoading(false);
      error = ErrorType.GENERIC;
    }

    openModal({ children: <ErrorPage errorType={error} /> });
  }, [closeAll, onVoteAgain, openModal, submitVotes, voteLimit]);

  // Automatically submit vote if a user logs in and there was a vote submission pending
  useEffect(() => {
    (async () => {
      if (user && pendingBracketVote) {
        setPendingBracketVote(false);
        await onSubmit();
      }
    })();
  }, [onSubmit, pendingBracketVote, user, setPendingBracketVote]);

  const Headline = () => {
    const headlineText = isDirty || allMatchupsVoted ? content.votesSubmittedHeader.headline : content.headline;

    return (
      <Heading className="VoteHeader__headline" as="h1" variant="emphasized" {...styles.headline} textAlign="center">
        {headlineText}
      </Heading>
    );
  };

  const Description = () => {
    const descriptionText =
      isDirty || allMatchupsVoted ? content.votesSubmittedHeader.description : content.description;

    return (
      <Text className="VoteHeader__description" allowMarkdown mb={3} {...styles.description} textAlign="center">
        {descriptionText}
      </Text>
    );
  };

  const VoteAction = () => {
    if (bracketType === 'BY_MATCHUP') {
      return null;
    }

    if (!isDirty) {
      return (
        <>
          <Button
            className="VoteHeader__btn-submit"
            isDisabled={!canSubmitBracket || isLoading}
            size={'lg'}
            mb={3}
            onClick={user ? onSubmit : handleLogin}
            {...styles.btnSubmit}>
            {content.submitBracketBtnText}
            {isLoading && <Spinner size="xs" display={'inline'} thickness={'1px'} ml={3} />}
          </Button>
          <Text className="VoteHeader__vote-progress" color="#7c7c7c" {...styles.voteProgress}>
            {totalAllocatedVotes} / {totalVotesRequired} {content.voteProgressText}
          </Text>
        </>
      );
    }

    return (
      <>
        {!hasReachedVoteLimit && (
          <Button className="VoteHeader__btn-vote-again" size={'lg'} mb={3} onClick={onVoteAgain} {...styles.btnSubmit}>
            {content.votesSubmittedHeader.voteAgainBtnText}
          </Button>
        )}
      </>
    );
  };

  return (
    <Container
      className="VoteHeader"
      sx={{ ':first-child': { pt: [4, 10] } }}
      centerContent
      mb={6}
      {...desktopStyles}
      {...styles.root}>
      <Headline />
      <Description />
      <VoteAction />
    </Container>
  );
};
